/**
 * Optional AES-GCM payload encryption.
 *
 * A 256-bit key is derived from the passphrase with PBKDF2-SHA-256. Each
 * record is sealed with a fresh 96-bit IV; the wire shape is
 * `{ __enc: 1, iv: number[], ct: number[] }` stored *in place of* the payload.
 * The primary key and `__version`/`__updatedAt` columns stay in clear text so
 * indexes and the sync-queue outbox keep working without a key.
 */

import { OfflineStoreError } from './types'
import type { EncryptionConfig } from './types'

const PBKDF2_DEFAULT_ITERS = 210_000
const enc = new TextEncoder()
const dec = new TextDecoder()

/** Sealed envelope persisted in IndexedDB when encryption is enabled. */
export interface EncryptedEnvelope {
	readonly __enc: 1
	readonly iv: number[]
	readonly ct: number[]
}

export function isEncryptedEnvelope(v: unknown): v is EncryptedEnvelope {
	return (
		typeof v === 'object' &&
		v !== null &&
		(v as { __enc?: unknown }).__enc === 1 &&
		Array.isArray((v as { ct?: unknown }).ct)
	)
}

function getSubtle(): SubtleCrypto {
	const c = (globalThis as { crypto?: Crypto }).crypto
	if (!c?.subtle) {
		throw new OfflineStoreError(
			'idb-unavailable',
			'WebCrypto SubtleCrypto unavailable; cannot use payload encryption',
		)
	}
	return c.subtle
}

/** Derive a non-extractable AES-GCM key from the passphrase (PBKDF2). */
export async function deriveKey(cfg: EncryptionConfig, dbName: string): Promise<CryptoKey> {
	const subtle = getSubtle()
	const salt = cfg.salt ?? enc.encode(`norkron:web-offline-store:${dbName}`)
	const baseKey = await subtle.importKey('raw', enc.encode(cfg.passphrase), 'PBKDF2', false, [
		'deriveKey',
	])
	return subtle.deriveKey(
		{
			name: 'PBKDF2',
			salt: salt as BufferSource,
			iterations: cfg.iterations ?? PBKDF2_DEFAULT_ITERS,
			hash: 'SHA-256',
		},
		baseKey,
		{ name: 'AES-GCM', length: 256 },
		false,
		['encrypt', 'decrypt'],
	)
}

/** Seal a structured payload into an {@link EncryptedEnvelope}. */
export async function sealPayload(key: CryptoKey, payload: unknown): Promise<EncryptedEnvelope> {
	const subtle = getSubtle()
	const iv = crypto.getRandomValues(new Uint8Array(12))
	const plaintext = enc.encode(JSON.stringify(payload))
	const ct = await subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext as BufferSource)
	return { __enc: 1, iv: Array.from(iv), ct: Array.from(new Uint8Array(ct)) }
}

/** Open an {@link EncryptedEnvelope}. Throws `decrypt-failed` on tamper/bad key. */
export async function openPayload(key: CryptoKey, env: EncryptedEnvelope): Promise<unknown> {
	const subtle = getSubtle()
	try {
		const plaintext = await subtle.decrypt(
			{ name: 'AES-GCM', iv: new Uint8Array(env.iv) },
			key,
			new Uint8Array(env.ct) as BufferSource,
		)
		return JSON.parse(dec.decode(plaintext))
	} catch (err) {
		throw new OfflineStoreError(
			'decrypt-failed',
			'AES-GCM decryption failed (wrong passphrase or tampered ciphertext)',
			{ cause: err },
		)
	}
}
